import Footer from "../../components/common/layout/Footer";
import ProductPlansDisplay from "@/components/features/shared/ProductPlansDisplay/ProductPlansDisplay";
import AnimatedSection from "../../components/common/animations/AnimatedSection";
import ParallaxSection from "../../components/common/animations/ParallaxSection";
import Floating3D from "../../components/common/animations/Floating3D";
import { useEffect, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { motion } from "framer-motion";
import { Check, Server, Zap, Shield, Globe, Database } from "lucide-react";
import FAQSection from "../../components/common/sections/FAQ";
import Breadcrumbs from "@/components/common/sections/Breadcrumbs";
import BreadcrumbsImage from "@/assets/images/domain/simpleSeo.svg";
import SEO from "@/components/common/seo/seo";

if (typeof window !== "undefined") {
    gsap.registerPlugin(ScrollTrigger);
}

export default function WordPressHosting() {
    const [plans, setPlans] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        // Load SEO plans from dummy data
        const loadPlans = () => {
            setLoading(true);
            setError(null);
            
            // Simulate API delay
            setTimeout(() => {
                const dummyPlans = [
                    {
                        id: 1,
                        name: "SEO Starter",
                        description: "Perfect for small businesses. Get started with professional SEO services.",
                        periods: [
                            { period: 1, price: { register: 49.99, renew: 69.99 } },
                            { period: 12, price: { register: 39.99, renew: 59.99 } },
                            { period: 24, price: { register: 34.99, renew: 54.99 } },
                            { period: 36, price: { register: 29.99, renew: 49.99 } }
                        ],
                        features: [
                            { name: "Keyword Research", value: "10 Keywords" },
                            { name: "On-Page Optimization", value: "5 Pages" },
                            { name: "Monthly Reports", value: "Yes" },
                            { name: "Support", value: "Email" }
                        ]
                    },
                    {
                        id: 2,
                        name: "SEO Business",
                        description: "Ideal for growing businesses. More comprehensive SEO services.",
                        periods: [
                            { period: 1, price: { register: 99.99, renew: 139.99 } },
                            { period: 12, price: { register: 79.99, renew: 119.99 } },
                            { period: 24, price: { register: 69.99, renew: 109.99 } },
                            { period: 36, price: { register: 59.99, renew: 99.99 } }
                        ],
                        features: [
                            { name: "Keyword Research", value: "25 Keywords" },
                            { name: "On-Page Optimization", value: "15 Pages" },
                            { name: "Monthly Reports", value: "Yes" },
                            { name: "Support", value: "Email & Phone" },
                            { name: "Link Building", value: "5 Links/Month" }
                        ]
                    },
                    {
                        id: 3,
                        name: "SEO Professional",
                        description: "For professional businesses. Maximum SEO services and support.",
                        periods: [
                            { period: 1, price: { register: 199.99, renew: 279.99 } },
                            { period: 12, price: { register: 159.99, renew: 239.99 } },
                            { period: 24, price: { register: 139.99, renew: 219.99 } },
                            { period: 36, price: { register: 119.99, renew: 199.99 } }
                        ],
                        features: [
                            { name: "Keyword Research", value: "Unlimited" },
                            { name: "On-Page Optimization", value: "Unlimited" },
                            { name: "Monthly Reports", value: "Yes" },
                            { name: "Support", value: "Priority" },
                            { name: "Link Building", value: "15 Links/Month" },
                            { name: "Content Strategy", value: "Yes" }
                        ]
                    }
                ];
                
                setPlans(dummyPlans);
                setLoading(false);
            }, 500);
        };

        loadPlans();
    }, []);

    useEffect(() => {
        // Animate sections on scroll
        const sections = document.querySelectorAll(".hosting-section");

        sections.forEach((section, index) => {
            gsap.fromTo(
                section,
                {
                    opacity: 0,
                    y: 60,
                    scale: 0.95,
                },
                {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    duration: 0.8,
                    delay: index * 0.15,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: section,
                        start: "top 80%",
                        toggleActions: "play none none none",
                    },
                }
            );
        });

        return () => {
            ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
        };
    }, [plans]);

    const handleSelectPlan = (plan: any, period: number, price: number) => {
        console.log(
            "Selected WordPress hosting plan:",
            plan,
            "Period:",
            period,
            "Price:",
            price
        );
        // TODO: Implement cart/checkout flow
    };

    const features = [
        {
            icon: Zap,
            title: "Optimized Performance",
            description:
                "Lightning-fast WordPress hosting with optimized servers for maximum speed.",
        },
        {
            icon: Shield,
            title: "Enhanced Security",
            description:
                "Advanced security features to protect your WordPress site from threats.",
        },
        {
            icon: Server,
            title: "Auto Updates",
            description:
                "Automatic WordPress core, plugin, and theme updates for peace of mind.",
        },
        {
            icon: Database,
            title: "Daily Backups",
            description:
                "Automated daily backups to ensure your data is always safe.",
        },
        {
            icon: Globe,
            title: "Easy Migration",
            description:
                "Free WordPress site migration service to get you started quickly.",
        },
        {
            icon: Check,
            title: "24/7 Support",
            description: "Expert WordPress support available around the clock.",
        },
    ];

    const simpleSEOFAQs = [
        {
            question: "What is Simple SEO?",
            answer:
                "Search Engine Optimization or SEO is a series of actions done to a website – or web page – to boost its visibility on search engines like Google, Yahoo, or Bing. Some of these actions include targeting relevant keywords, fixing broken links, and more.<br /><br /><strong>Simple SEO</strong> is our intuitive tool built to improve your website's SEO score. It provides actionable tips and reports to make your site search engine-friendly.",
        },
        {
            question: "Why are keywords important? How do I choose the right one?",
            answer:
                'Keywords refer to words and phrases that online users enter into search engines. They can be generic, short-tail keywords consisting of one to two words or more specific long-tail keywords.<br /><br />Simple SEO analyses keywords your competitors are ranking for. The goal is to determine their keyword strategy – and how you can "reverse-engineer" it for your website.',
        },
        {
            question: "How to add a keyword in Simple SEO?",
            answer:
                'Log in to your Account Manager and go to Simple SEO. Then, click Manage to log in to your Online Marketing Hub account. Look for the "Open" button in your Business Profile Progress. Finally, add your keywords to the allocated field.',
        },
        {
            question: "How do I get to the first page of search engines?",
            answer:
                "You can get to the first page of search engines in at least six months of applying technical, on-page, and off-age SEO strategies. Of course, this is not guaranteed and requires continuous effort.<br /><br />Simple SEO helps you achieve this by providing step-by-step SEO actions. You can also run regular health checkups on your website to determine what areas need to be optimised.",
        },
        {
            question: "Will you manage SEO for me?",
            answer:
                "While Simple SEO is a do-it-yourself solution, we also offer a do-it-for-you service.<br /><br />With Managed SEO, you get a dedicated specialist to optimise your site – from fixing broken links, keyword research, and more. You'll also get progress reports to compare with your initial SEO scores.",
        },
    ];

    return (
        <>
            <SEO
                title="Simple SEO - SAN micron"
                description="Enhance your website's visibility with SAN micron's Simple SEO services. Our user-friendly tools and expert strategies help you optimize your site for search engines, driving more traffic and boosting your online presence."
            />
            <div className="relative overflow-hidden min-h-screen text-white">
                {/* <Header /> */}
                <Breadcrumbs
                    title="Simple SEO"
                    subtitle=""
                    breadcrumbText="Simple SEO"
                    backgroundImage={BreadcrumbsImage}
                />

                {/* Hero Section */}
                <ParallaxSection speed={0.25}>
                    <Floating3D intensity={18}>
                        <section className="hosting-section container py-20 pt-32">
                            <div className="text-center max-w-4xl mx-auto">
                                {/* <h1 className="text-5xl md:text-6xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-brandStart to-brandEnd">
                                    Simple SEO
                                </h1> */}
                                <p className="text-xl text-textColor mb-8 leading-relaxed">
                                    Be found on search engines.
                                </p>
                            </div>
                        </section>
                    </Floating3D>
                </ParallaxSection>

                {/* Pricing Plans */}
                <AnimatedSection delay={0.2} direction="up">
                    <div className="hosting-section">
                        <ProductPlansDisplay
                            plans={plans}
                            loading={loading}
                            error={error}
                            currency="USD"
                            onSelectPlan={handleSelectPlan}
                            buttonText="Get Started"
                            priceLabel="per month"
                        />
                    </div>
                </AnimatedSection>

                {/* Features Section */}
                <ParallaxSection speed={0.3} direction="down">
                    <AnimatedSection delay={0.3} direction="left">
                        <section className="hosting-section container py-20">
                            <div className="text-center mb-16">
                                <h2 className="text-4xl font-bold mb-4">
                                    Why Choose WordPress Hosting?
                                </h2>
                                <p className="text-textColor text-lg">
                                    Everything you need for a powerful WordPress website
                                </p>
                            </div>
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {features.map((feature, index) => {
                                    const Icon = feature.icon;
                                    return (
                                        <motion.div
                                            key={index}
                                            className="bg-cardBg/90 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-primary/30 transition-all"
                                            whileHover={{ scale: 1.02, y: -5 }}
                                            transition={{ type: "spring", stiffness: 300 }}
                                        >
                                            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-brandStart to-brandEnd flex items-center justify-center mb-4">
                                                <Icon className="w-6 h-6 text-white" />
                                            </div>
                                            <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                                            <p className="text-textColor">{feature.description}</p>
                                        </motion.div>
                                    );
                                })}
                            </div>
                        </section>
                    </AnimatedSection>
                </ParallaxSection>

                {/* FAQ Section */}
                <AnimatedSection delay={0.6}>
                    <div className="abstract-bg hosting-section">
                        <FAQSection faqs={simpleSEOFAQs} />
                    </div>
                </AnimatedSection>

                <AnimatedSection delay={0.7}>
                    <Footer />
                </AnimatedSection>
            </div>
        </>
    );
}
